import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Bell, 
  Clock, 
  AlertTriangle, 
  Plus, 
  Trash2, 
  Edit2, 
  Play,
  CheckCircle,
  XCircle,
  MessageSquare,
  Mail,
  Smartphone,
  Calendar,
  TrendingUp,
  RefreshCw,
  Settings,
  Zap
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReminderAutomation = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('rules');
  const [rules, setRules] = useState([]);
  const [escalationRules, setEscalationRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddRule, setShowAddRule] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  // New rule form state
  const [newRule, setNewRule] = useState({
    name: '',
    description: '',
    trigger_type: 'days_before_due',
    trigger_days: 5,
    channels: ['whatsapp'],
    applies_to: 'tasks',
    custom_message: '',
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [rulesRes, escalationRes, logsRes, statsRes, schedulerRes] = await Promise.all([
        axios.get(`${API_URL}/api/reminder-rules`, { headers }),
        axios.get(`${API_URL}/api/escalation-rules`, { headers }),
        axios.get(`${API_URL}/api/reminder-logs?limit=50`, { headers }),
        axios.get(`${API_URL}/api/compliance/stats`, { headers }),
        axios.get(`${API_URL}/api/compliance/scheduler-status`, { headers })
      ]);

      setRules(rulesRes.data || []);
      setEscalationRules(escalationRes.data || []);
      setLogs(logsRes.data || []);
      setStats(statsRes.data);
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load reminder automation data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/reminder-rules`, newRule, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "Reminder rule created successfully"
      });
      setShowAddRule(false);
      setNewRule({
        name: '',
        description: '',
        trigger_type: 'days_before_due',
        trigger_days: 5,
        channels: ['whatsapp'],
        applies_to: 'tasks',
        custom_message: '',
        is_active: true
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create reminder rule",
        variant: "destructive"
      });
    }
  };

  const handleUpdateRule = async (ruleId, updates) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/reminder-rules/${ruleId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "Reminder rule updated"
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update rule",
        variant: "destructive"
      });
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/reminder-rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "Reminder rule deleted"
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete rule",
        variant: "destructive"
      });
    }
  };

  const handleTriggerReminders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/compliance/trigger-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: `Triggered ${response.data.reminders_sent} reminders`
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to trigger reminders",
        variant: "destructive"
      });
    }
  };

  const handleTriggerScheduler = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/compliance/trigger-scheduler`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Success",
        description: "Compliance scheduler triggered"
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to trigger scheduler",
        variant: "destructive"
      });
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'whatsapp': return <MessageSquare className="w-4 h-4 text-green-500" />;
      case 'email': return <Mail className="w-4 h-4 text-blue-500" />;
      case 'sms': return <Smartphone className="w-4 h-4 text-purple-500" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  const getTriggerLabel = (type) => {
    switch (type) {
      case 'days_before_due': return 'Days Before Due';
      case 'days_after_overdue': return 'Days After Overdue';
      case 'on_task_creation': return 'On Task Creation';
      case 'on_invoice_creation': return 'On Invoice Creation';
      case 'on_invoice_overdue': return 'On Invoice Overdue';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Reminder & Automation Panel</h1>
          <p className="text-gray-500">Configure automated reminders, escalations, and compliance rules</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleTriggerReminders}>
            <Play className="w-4 h-4 mr-2" />
            Run Reminders Now
          </Button>
          <Button onClick={() => setShowAddRule(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Rule
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Tasks</p>
                <p className="text-2xl font-bold">{stats?.total_pending_tasks || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Overdue Tasks</p>
                <p className="text-2xl font-bold text-red-600">{stats?.overdue_tasks || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Due This Week</p>
                <p className="text-2xl font-bold text-orange-600">{stats?.tasks_due_this_week || 0}</p>
              </div>
              <Calendar className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Reminders Sent Today</p>
                <p className="text-2xl font-bold text-green-600">{stats?.reminders_sent_today || 0}</p>
              </div>
              <Bell className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scheduler Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Automation Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Badge variant={schedulerStatus?.status === 'active' ? 'default' : 'secondary'}>
              {schedulerStatus?.status === 'active' ? 'Active' : 'Inactive'}
            </Badge>
            <span className="text-sm text-gray-500">
              Last Run: {schedulerStatus?.scheduler?.last_run 
                ? new Date(schedulerStatus.scheduler.last_run).toLocaleString() 
                : 'Never'}
            </span>
            <span className="text-sm text-gray-500">
              Tasks Generated: {schedulerStatus?.scheduler?.stats?.tasks_generated || 0}
            </span>
            <span className="text-sm text-gray-500">
              Reminders Sent: {schedulerStatus?.scheduler?.stats?.reminders_sent || 0}
            </span>
            <Button variant="outline" size="sm" onClick={handleTriggerScheduler}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Trigger Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="rules">
            <Bell className="w-4 h-4 mr-2" />
            Reminder Rules ({rules.length})
          </TabsTrigger>
          <TabsTrigger value="escalations">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Escalation Rules ({escalationRules.length})
          </TabsTrigger>
          <TabsTrigger value="logs">
            <TrendingUp className="w-4 h-4 mr-2" />
            Activity Logs ({logs.length})
          </TabsTrigger>
        </TabsList>

        {/* Reminder Rules Tab */}
        <TabsContent value="rules" className="space-y-4">
          {rules.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bell className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium mb-2">No Reminder Rules</h3>
                <p className="text-gray-500 mb-4">Create your first reminder rule to automate notifications</p>
                <Button onClick={() => setShowAddRule(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Rule
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {rules.map((rule) => (
                <Card key={rule.id}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Switch
                          checked={rule.is_active}
                          onCheckedChange={(checked) => handleUpdateRule(rule.id, { is_active: checked })}
                        />
                        <div>
                          <h3 className="font-medium">{rule.name}</h3>
                          <p className="text-sm text-gray-500">{rule.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline">
                              {getTriggerLabel(rule.trigger_type)}
                            </Badge>
                            <Badge variant="secondary">
                              {rule.trigger_days} days
                            </Badge>
                            <Badge variant="secondary">
                              {rule.applies_to}
                            </Badge>
                            <div className="flex gap-1">
                              {rule.channels?.map((ch) => (
                                <span key={ch}>{getChannelIcon(ch)}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingRule(rule)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteRule(rule.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Escalation Rules Tab */}
        <TabsContent value="escalations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Escalation Matrix</CardTitle>
              <CardDescription>
                Define escalation rules for overdue tasks and invoices
              </CardDescription>
            </CardHeader>
            <CardContent>
              {escalationRules.length === 0 ? (
                <div className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No escalation rules configured</p>
                  <Button variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Escalation Rule
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {escalationRules.map((rule) => (
                    <div key={rule.id} className="border rounded-lg p-4">
                      <h4 className="font-medium">{rule.name}</h4>
                      <p className="text-sm text-gray-500">{rule.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Default Escalation Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Default Escalation Logic</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                  <Badge className="bg-yellow-500">Level 1</Badge>
                  <span>1-3 days overdue → Notify assigned staff</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                  <Badge className="bg-orange-500">Level 2</Badge>
                  <span>4-7 days overdue → Notify team lead + admin</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                  <Badge className="bg-red-500">Level 3</Badge>
                  <span>7+ days overdue → Notify owner + mark critical</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>
                History of sent reminders and notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-8">
                  <TrendingUp className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No reminder logs yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        {log.status === 'sent' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        {getChannelIcon(log.channel)}
                        <div>
                          <p className="font-medium text-sm">{log.client_name}</p>
                          <p className="text-xs text-gray-500">{log.message_sent?.substring(0, 60)}...</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={log.status === 'sent' ? 'default' : 'destructive'}>
                          {log.status}
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(log.sent_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Rule Modal */}
      {showAddRule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle>Create Reminder Rule</CardTitle>
              <CardDescription>Define when and how reminders should be sent</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Rule Name</Label>
                <Input
                  value={newRule.name}
                  onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                  placeholder="e.g., 5 Days Before Due Date"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={newRule.description}
                  onChange={(e) => setNewRule({ ...newRule, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Trigger Type</Label>
                  <Select
                    value={newRule.trigger_type}
                    onValueChange={(value) => setNewRule({ ...newRule, trigger_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days_before_due">Days Before Due</SelectItem>
                      <SelectItem value="days_after_overdue">Days After Overdue</SelectItem>
                      <SelectItem value="on_task_creation">On Task Creation</SelectItem>
                      <SelectItem value="on_invoice_creation">On Invoice Creation</SelectItem>
                      <SelectItem value="on_invoice_overdue">On Invoice Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Trigger Days</Label>
                  <Input
                    type="number"
                    value={newRule.trigger_days}
                    onChange={(e) => setNewRule({ ...newRule, trigger_days: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
              </div>

              <div>
                <Label>Applies To</Label>
                <Select
                  value={newRule.applies_to}
                  onValueChange={(value) => setNewRule({ ...newRule, applies_to: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All (Tasks + Invoices)</SelectItem>
                    <SelectItem value="tasks">Tasks Only</SelectItem>
                    <SelectItem value="invoices">Invoices Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Channels</Label>
                <div className="flex gap-4 mt-2">
                  {['whatsapp', 'email', 'sms'].map((channel) => (
                    <label key={channel} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newRule.channels.includes(channel)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewRule({ ...newRule, channels: [...newRule.channels, channel] });
                          } else {
                            setNewRule({ ...newRule, channels: newRule.channels.filter(c => c !== channel) });
                          }
                        }}
                        className="rounded"
                      />
                      <span className="flex items-center gap-1">
                        {getChannelIcon(channel)}
                        <span className="capitalize">{channel}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label>Custom Message (Optional)</Label>
                <Input
                  value={newRule.custom_message}
                  onChange={(e) => setNewRule({ ...newRule, custom_message: e.target.value })}
                  placeholder="Leave empty for default message"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={newRule.is_active}
                  onCheckedChange={(checked) => setNewRule({ ...newRule, is_active: checked })}
                />
                <Label>Active</Label>
              </div>
            </CardContent>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setShowAddRule(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRule} disabled={!newRule.name}>
                Create Rule
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReminderAutomation;
